// Copyright 2018 Amazon.com, Inc. or its affiliates. All Rights Reserved.
// Licensed under the Amazon Software License
// http://aws.amazon.com/asl/

import _ from 'lodash';
import { types } from 'mobx-state-tree';
import { RestAPI as API } from '@aws-amplify/api-rest';

import { API_NAME } from '../../../../utils';
import { AmplifyAPIRequestRecycler } from '../../../../utilities/fetchUtilities';
// import { QueryHelper } from '../../helpers/query-helper';
import { toInternal } from '../../helpers/transformers';
import { boom } from '../../helpers/errors';

// ==================================================================
// GeneSearch
// ==================================================================
const GeneSearch = types.model('GeneSearch', {
  type: 'gene',
  input: '', // this value is the gene search term while the user is entering the value
  //searched: '', // this value is the gene search term after the search was successfully performed
  searched: types.optional(types.array(types.string), []), // this value is the gene search term after the search was successfully performed
  processing: false,
  error: types.maybe(types.frozen()),
})

.actions(() => ({
  // I had issues using runInAction from mobx
  // the issue is discussed here https://github.com/mobxjs/mobx-state-tree/issues/915
  runInAction(fn) {
    return fn();
  }
}))

.actions(self => ({

  doSearch: async() => {
    self.searched.clear();
    self.input = _.trim(self.input);
    if (_.isEmpty(self.input)) {
      self.error = boom.badRequest('The gene name can not be empty');
      throw self.error;
    }
    let filteredGenes = [
      "AKT3", "APC", "ATM", "BRAF", "CDH23", "DEPDC5", 
      "KCNQ1", "MYO6", "TCF12", "TCF4", "UBE3A", "USH2A"
    ];
    if (_.find(filteredGenes, gene => { return self.input === gene })) {
      self.error = boom.badRequest('This gene is not yet supported for variant prioritization.');
      throw self.error;
    }
    self.processing = true;
    self.error = undefined;
    try {
      const requestRecycler = new AmplifyAPIRequestRecycler();
      let hgncGroupList = [];
      let urlPartition = ""; // URLs are split by group index; 0-99 per gene
      for (let batch = 0; batch < 100; batch++) {
        urlPartition = `/vpt/search?hgnc_gr=${self.input}_${batch}`;
        hgncGroupList.push(urlPartition);
      }
      const newPromise = async (hgnc_group) => {
        const response = await requestRecycler.capture(API.get(API_NAME, hgnc_group));
        return response;
      };

      //const url = `/vpt/search?=${self.input}`;
      const promiseList = hgncGroupList.map(hgnc_group => newPromise(hgnc_group));
      return Promise.all(promiseList).then((result) => {
        const resultData = [];
        result.forEach((obj) => {
          resultData.push(obj.data);
        });
        let flatData = resultData.flat();
        flatData = _.uniqBy(flatData, 'caId');
        self.runInAction(() => {
          self.processing = false;
          self.searched.replace([self.input]);
        });
        return toInternal(flatData);
      }).catch(err => { 
        self.runInAction(() => {
          self.processing = false;
          self.error = boom.badRequest('Something went wrong with the request. Enter a supported gene symbol or please try again. ' + err);
        });
        throw self.error;
      });
      // const queryHelper = new QueryHelper();
      // const result = await queryHelper.getGeneVariants({ geneName: self.input });
      // self.runInAction(() => {
      //   self.processing = false;
      //   self.searched.replace([self.input]);
      // });
      // return result;
    } catch (error) {
      if (API.isCancel(error)) {
        console.log('Cancel');
      }
      self.runInAction(() => {
        self.processing = false;
        self.error = error;
      });
      throw error;
    }
  },

  setInput: (text) => {
    self.input = _.trim(text);
    self.error = undefined;
  },

  isSame: (search = {}) => {
    if (_.size(search.searched) !== _.size(self.searched)) return false;
    const diff = _.difference(_.slice(search.searched), _.slice(self.searched));
    return  search.type === self.type && (_.isEmpty(diff));
  },

  clear: () => {
    self.input = '';
    self.searched = [];
    self.processing = false;
    self.error = undefined;
  }

}))

.views(self => ({
  get errorMessage() {
    if (!self.error) return '';
    return (_.get(self.error, 'friendly') || _.get(self.error, 'message') || 'Something went wrong while contacting the server when search by gene, please try again later');
  },
  get searchInput() {
    return {
      type: self.type,
      searched: _.slice(self.searched) || [],
    };
  },
 
}));

export { GeneSearch };
